import { CalendarClock, MapPin, ShoppingBag, Volleyball } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Paperclip2, SaveAdd, TicketDiscount } from 'iconsax-react';
import { getBookingDetailsByQuery, paymentOrderId } from '../Utils/API';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import RazerPayCapture from './RazerPayCapture';

function BookingDetailsByQuery() {
    const [searchParams] = useSearchParams();
    const queryBookingId =
        searchParams.get('order_id') ||
        searchParams.get('booking_id') ||
        searchParams.get('bookingId') ||
        searchParams.get('orderId');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [venueImage, setVenueImage] = useState(null);
    const [showRetryButton, setShowRetryButton] = useState(false);
    const [orderState, setOrderState] = useState({});
    const Userdetails = useSelector(state => state.user?.userDetails);

    const generateOrderId = async (amount, bookingType = "court", bookingId) => {
        const { status, order_id, currency } = await paymentOrderId({
            amount: Math.round(amount * 100),
            currency: 'INR',
            booking_type: bookingType,
            booking_id: bookingId
        });

        if (status === "created") {
            setOrderState({ order_id, currency, status, amount, Userdetails, booking_id: bookingId });
        } else {
            toast.error("Payment failed");
        }
    };

    const retryPayment = () => {
        if (data?.transaction_log?.length == 0) {
            generateOrderId(data?.partial_payment ? data?.partial_total_price : data?.total_price, "court", data?.id);
        } else {
            setOrderState({
                order_id: data.transaction_log[0]?.order_id,
                currency: 'INR',
                status: 'created',
                amount: data.partial_payment ? data?.partial_total_price : data?.total_price,
                Userdetails: Userdetails,
                booking_id: data?.id
            });
        }
    };

    useEffect(() => {
        const fetchBooking = async () => {
            if (!queryBookingId) {
                setError('Missing booking reference in the URL.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await getBookingDetailsByQuery(queryBookingId);
                const bookingPayload = response?.data ? response.data : response;
                const bookingData = bookingPayload?.data ? bookingPayload.data : bookingPayload;
                setData(bookingData);
            } catch (err) {
                console.log('error', err);
                setError('Unable to fetch booking details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchBooking();
    }, [queryBookingId]);

    useEffect(() => {
        if (data) {
            const actualStartDateTimeStr = `${data?.booking_date} ${data?.time_slot?.split(' - ')[0]}`;
            const actualBookingTimeObject = moment(actualStartDateTimeStr, 'YYYY-MM-DD HH:mm A');
            const isBookingTimePassed = actualBookingTimeObject.isBefore(moment());
            const isThreeMinutesPassed = moment().diff(moment(data?.create_datetime).add(3, 'minutes'), 'minutes') < 3 ? false : true;
            const showButton = isThreeMinutesPassed ? false : data?.payment_status == 'Pending' && !isBookingTimePassed;
            setShowRetryButton(showButton);
            const imageData = JSON.parse(data?.venue?.profile_image || '[]');
            if (imageData && imageData?.length > 0) {
                setVenueImage(imageData[0]?.media_url);
            }
        } else {
            setVenueImage(null);
        }
    }, [data]);

    const refundSummary = React.useMemo(() => {
        if (!data?.cancelled && data?.payment_status !== 'Cancelled') {
            return null;
        }

        const paidAmount = Number(data?.amount_paid || 0);
        const convenienceFee = Number(data?.platform_fee ?? data?.partial_platform_fee ?? 0);
        const refundAmount = Math.max(paidAmount - convenienceFee, 0);

        return {
            refundAmount: refundAmount.toFixed(2),
            convenienceFee: convenienceFee.toFixed(2),
            amountPaid: paidAmount.toFixed(2),
        };
    }, [data]);

    const slotPriceDisplay = Number(data?.total_slot_price ?? data?.slot_price ?? data?.partial_total_slot_price ?? 0);
    const addonPriceDisplay = Number(data?.addon_price ?? data?.partial_addon_price ?? 0);
    const discountDisplay = Number(data?.discount_price ?? data?.partial_discount_price ?? 0);
    const platformDisplay = Number(data?.platform_fee ?? data?.partial_platform_fee ?? 0);

    // Admin booking logic
    const isAdminBooking = data?.booked_from === "ADMIN";
    const hasCustomPrice = data?.custom_total_price !== null && data?.custom_total_price !== undefined;
    const calculatedTotal = Number(data?.total_price || 0);
    const customPrice = hasCustomPrice ? Number(data?.custom_total_price) : null;
    const displayGrandTotal = (isAdminBooking && hasCustomPrice) ? customPrice : calculatedTotal;
    const priceDifference = hasCustomPrice ? customPrice - calculatedTotal : 0;
    const showAdditionalCharges = priceDifference > 0; // Extra charge
    const showAdminDiscount = priceDifference < 0; // Discount given
    const adminDiscountAmount = Math.abs(priceDifference);
    const showAmountPaid = !isAdminBooking;

    const needToPay = data?.payment_status !== "Pending"
        ? Number(Number(data?.total_price || 0) - Number(data?.partial_total_price || 0)).toFixed(2)
        : data?.total_price;

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center mt-4">
                <div className="w-full max-w-4xl bg-white shadow-xl rounded-lg p-6 text-center">
                    <p className="text-gray-600">Loading booking details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col justify-center items-center mt-4">
                <div className="w-full max-w-4xl bg-white shadow-xl rounded-lg p-6 text-center">
                    <p className="text-red-500 font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <div className="flex flex-col justify-center items-center mt-4">
            <div className="w-full max-w-4xl bg-white shadow-xl rounded-lg p-6 space-y-6 hover:shadow-2xl transition-shadow duration-300 ease-in-out booking-details">
                <div className="border-b-2 pb-4 space-y-2 border-gray-200">
                    <div className='flex flex-row justify-between items-center border-b border-gray-300 border-dashed pb-2 mb-4'>
                        <p className="capitalize font-semibold text-red-500 text-lg">{data?.booking_id}</p>
                        <div className="flex justify-center items-center w-auto h-full">
                            {(() => {
                                // Use activity_log[0]?.status if available, otherwise fall back to payment_status
                                const officialStatus = data?.activity_log?.[0]?.status;
                                const rawStatus = officialStatus || data?.payment_status;
                                // Convert "Completed" to "Paid" for display
                                const displayStatus = rawStatus === 'Completed' ? 'Paid' : rawStatus;
                                return (
                                    <p
                                        className={`font-semibold text-sm px-2 py-1 rounded-md flex justify-center items-center ${
                                            displayStatus === 'Paid'
                                                ? 'bg-green-200 text-green-600'
                                                : displayStatus === 'Pending'
                                                    ? 'bg-yellow-200 text-yellow-600'
                                                    : displayStatus === 'Partially Paid' || displayStatus === 'RS. Partial Payment'
                                                        ? 'bg-violet-100 text-violet-600'
                                                        : 'bg-red-200 text-red-600'
                                        }`}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        {displayStatus}
                                    </p>
                                );
                            })()}
                        </div>
                    </div>
                    <div className='flex flex-col sm:flex-row gap-4'>
                        <div>
                            <img className='h-24 w-40 rounded-md shadow bg-gray-200' src={venueImage} alt="Venue Image" />
                        </div>
                        <div className='flex flex-col gap-1.5'>
                            <p className="font-bold text-2xl text-gray-800">{data?.venue_name}</p>
                            <div className='flex items-start gap-2'>
                                <MapPin strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">{data?.venue?.address}</p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <CalendarClock strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">
                                    {data?.time_slot} • {moment(data?.booking_date).format("MMMM DD, YYYY")}
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Volleyball strokeWidth={1.25} size={20} color='red' />
                                <p className="text-sm text-gray-500">{data?.sport_name}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pb-4">
                    <p className="font-semibold text-lg text-gray-800">User Details</p>
                    <div className="mt-4 text-sm flex justify-between items-center border-b pb-2 border-gray-100">
                        <p className="font-semibold text-gray-800">Full Name</p>
                        <p className="text-black font-semibold">{`${data?.user?.first_name || ''} ${data?.user?.last_name || ''}`.trim() || 'N/A'}</p>
                    </div>
                    <div className="text-sm flex justify-between items-center border-b pb-2 border-gray-100">
                        <p className="font-semibold text-gray-800">Email</p>
                        <p className="text-black font-semibold">{data?.user?.email || 'N/A'}</p>
                    </div>
                    <div className="text-sm flex justify-between items-center border-b pb-2 border-gray-100">
                        <p className="font-semibold text-gray-800">Contact Number</p>
                        <p className="text-black font-semibold">{data?.user?.phone || 'N/A'}</p>
                    </div>
                </div>
                <div className="pb-4">
                    <p className="font-semibold text-lg text-gray-800">Payment Details</p>
                    <div className="mt-4 space-y-3 text-sm">
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <ShoppingBag size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Slot Price</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {Number(slotPriceDisplay).toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <SaveAdd size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Addons Price</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {Number(addonPriceDisplay).toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-100 hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <TicketDiscount size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Discount Amount</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {Number(discountDisplay).toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center border-b pb-2 border-gray-300 border-dashed hover:bg-gray-50 transition-all duration-200">
                            <div className="flex items-center gap-3">
                                <Paperclip2 size={20} color="red" strokeWidth={1.25} />
                                <p className="font-semibold text-gray-800">Convenience Fee</p>
                            </div>
                            <p className="font-semibold text-gray-800">₹ {Number(platformDisplay).toFixed(2)}</p>
                        </div>
                        <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                            <p className="font-semibold text-gray-800">Sub Total</p>
                            <p className="font-semibold text-gray-800">₹ {Number(calculatedTotal).toFixed(2)}</p>
                        </div>
                        {showAdditionalCharges && (
                            <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                                <p className="font-semibold text-gray-800">Additional Charges</p>
                                <p className="font-semibold text-gray-800">₹ {Number(priceDifference).toFixed(2)}</p>
                            </div>
                        )}
                        {showAdminDiscount && (
                            <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                                <p className="font-semibold text-gray-800">Discount</p>
                                <p className="font-semibold text-green-600">- ₹ {Number(adminDiscountAmount).toFixed(2)}</p>
                            </div>
                        )}
                        {showAmountPaid && (
                            <div className="flex justify-between items-center hover:bg-gray-50 transition-all duration-200">
                                <p className="font-semibold text-gray-800">Amount Paid</p>
                                <p className="font-semibold text-gray-800">₹ {data?.amount_paid}</p>
                            </div>
                        )}
                        {showAmountPaid && (
                            <div className="flex justify-between items-center py-2 border-t border-dashed border-slate-200 hover:bg-gray-50 transition-all duration-200">
                                <p className="font-semibold text-gray-800">Remaining Amount</p>
                                <p className="font-semibold text-gray-800">₹ {(Number(data?.total_price - data?.amount_paid)).toFixed(2)}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-between items-center text-red-500 border-t border-dashed pt-4 border-gray-200">
                    <p className="font-semibold text-xl">Grand Total</p>
                    <p className="font-semibold text-xl text-red-600">₹ {Number(displayGrandTotal).toFixed(2)}</p>
                </div>

                {refundSummary && data?.payment_status === 'Cancelled' && (
                    <div className="pt-6 border-t border-gray-200">
                        <p className="font-semibold text-lg text-gray-800 mb-3">Refund Details</p>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Amount Paid</p>
                                <p className="font-semibold">₹ {refundSummary.amountPaid}</p>
                            </div>
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Convenience Fee</p>
                                <p className="text-red-500">- ₹ {refundSummary.convenienceFee}</p>
                            </div>
                            <div className="border-t border-dashed" />
                            <div className="flex justify-between">
                                <p className="font-semibold text-gray-700">Refund Amount</p>
                                <p className="font-semibold text-green-600">₹ {refundSummary.refundAmount}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* RS. Partial Payment Section - For reschedule with higher price */}
                {data?.payment_status === 'RS. Partial Payment' && (() => {
                    // Calculate actual paid amount from multiple sources
                    let actualPaidAmount = 0;

                    // First try: Get from captured transactions (amount is in paise)
                    const capturedAmount = data?.transaction_log?.reduce((acc, item) => {
                        if (item?.payment_status === 'captured') {
                            return acc + Number(item?.amount || 0) / 100; // Convert paise to INR
                        }
                        return acc;
                    }, 0) || 0;

                    if (capturedAmount > 0) {
                        // Subtract any refunds from activity_log
                        const refundedAmount = data?.activity_log?.reduce((acc, item) => {
                            if (item?.activity_type === 'REFUND' && item?.status === 'Completed') {
                                return acc + Number(item?.amount || 0);
                            }
                            return acc;
                        }, 0) || 0;
                        actualPaidAmount = capturedAmount - refundedAmount;
                    } else {
                        // Fallback: Calculate from activity_log (payments - refunds)
                        const paymentsFromActivity = data?.activity_log?.reduce((acc, item) => {
                            if (item?.activity_type === 'PAYMENT' && item?.status === 'Completed') {
                                return acc + Number(item?.amount || 0);
                            }
                            if (item?.activity_type === 'BOOKING' && item?.status === 'Completed') {
                                return acc + Number(item?.amount || 0);
                            }
                            return acc;
                        }, 0) || 0;

                        const refundsFromActivity = data?.activity_log?.reduce((acc, item) => {
                            if (item?.activity_type === 'REFUND' && item?.status === 'Completed') {
                                return acc + Number(item?.amount || 0);
                            }
                            return acc;
                        }, 0) || 0;

                        actualPaidAmount = paymentsFromActivity - refundsFromActivity;
                    }

                    const totalDue = Number(data?.total_price || 0);
                    const remainingAmount = totalDue - actualPaidAmount;

                    return (
                        <div className="pt-6 border-t border-gray-200">
                            <p className="font-semibold text-lg text-gray-800 mb-3">
                                Reschedule Payment Details
                                <span className="ml-2 text-sm font-normal text-orange-500">
                                    (₹{actualPaidAmount.toFixed(2)} / ₹{totalDue.toFixed(2)})
                                </span>
                            </p>
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <p className="font-semibold text-gray-700">Amount Received</p>
                                    <p className="font-semibold text-green-600">₹ {actualPaidAmount.toFixed(2)}</p>
                                </div>
                                <div className="flex justify-between">
                                    <p className="font-semibold text-gray-700">New Total (After Reschedule)</p>
                                    <p className="font-semibold">₹ {totalDue.toFixed(2)}</p>
                                </div>
                                <div className="border-t border-orange-200 pt-2">
                                    <div className="flex justify-between">
                                        <p className="font-semibold text-gray-700">Pending Amount</p>
                                        <p className="font-semibold text-orange-600">₹ {remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Partial Payment Section - For initial partial payments */}
                {data?.partial_payment && data?.payment_status !== 'RS. Partial Payment' && (() => {
                    // Calculate actual paid amount from partial_booking_details
                    const actualPaidAmount = data?.partial_booking_details?.reduce((acc, item) => {
                        if (item?.payment_status === 'Paid') {
                            return acc + Number(item?.amount || 0);
                        }
                        return acc;
                    }, 0) || Number(data?.amount_paid || 0);

                    const totalDue = Number(data?.total_price || 0);
                    const remainingAmount = totalDue - actualPaidAmount;

                    return (
                        <div className="pt-6 border-t border-gray-200">
                            <p className="font-semibold text-lg text-gray-800 mb-3">
                                Partial Payment Summary
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    (₹{actualPaidAmount.toFixed(2)} / ₹{totalDue.toFixed(2)})
                                </span>
                            </p>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <p className="font-semibold text-gray-700">Amount Paid</p>
                                    <p className="font-semibold text-green-600">₹ {actualPaidAmount.toFixed(2)}</p>
                                </div>
                                <div className="flex justify-between">
                                    <p className="font-semibold text-gray-700">Need To Pay</p>
                                    <p className="font-semibold text-orange-600">₹ {remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}</p>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
            {showRetryButton && <div className='flex w-full max-w-4xl flex-row justify-end items-center'>
                <button
                    disabled={!showRetryButton}
                    onClick={retryPayment}
                    className="mt-4 bg-red-500 text-white py-2 px-4 rounded-full hover:bg-red-600 transition-all duration-200 exclude-from-pdf"
                >
                    Retry Payment
                </button>
                <RazerPayCapture orderState={orderState} />
            </div>}
        </div>
    );
}

export default BookingDetailsByQuery;
